import React from 'react';
import {
  Plugins,
  Atom,
  AtomRenderProps,
  type StyleAttributeObject,
} from '@packages/systems/wfdl';
import {SHARED_FORM_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';

import {TTypeConstructor, TTypeApplication} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {usePageId} from '@packages/systems/render';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const setDisplayNone = (style: StyleAttributeObject | undefined) => {
  if (style === undefined) {
    return {display: 'none'};
  } else if (style.display === 'none') {
    return style;
  } else {
    return {
      ...style,
      display: 'none',
    };
  }
};

const FormForm = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    renderChild,
    context,
    data,
    elementId,
  }: AtomRenderProps) => {
    const pageId = usePageId();
    const localeId = context.get('currentLocaleId');

    const newAttributes = attributes.filter((v, k) => {
      switch (k) {
        case 'action':
        case 'redirect':
        case 'data-redirect':
          return v !== '';
        // Typed as a string attribute, but WFDL evaluates the setting to a boolean.
        case 'data-wf-close-modal':
          return String(v) === 'true';
        default:
          return true;
      }
    });
    const siteKey = context.get('turnstileSiteKey');
    const props = generateAttributes(newAttributes, classes);

    const finalProps =
      context.get('formState') === 'success'
        ? {
            ...props,
            style: setDisplayNone(props.style),
          }
        : props;

    // Add additional information about the element that we want to render
    finalProps['data-wf-page-id'] = pageId;
    finalProps['data-wf-element-id'] = elementId;
    if (siteKey) {
      finalProps['data-turnstile-sitekey'] = siteKey;
    }
    if (localeId) {
      finalProps['data-wf-locale-id'] = localeId;
    }

    // The `data-wf-flow` property indicates that this form is related to a Logic Flow
    // In this case, we want to remove the `action` property so that the form HTTP action
    // is not triggered during the form submission on the published site - just the Logic Flow
    if (finalProps['data-wf-flow'] && finalProps.action) {
      // eslint-disable-next-line webflow/no-delete
      delete finalProps.action;
    }

    return (
      <form {...finalProps}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child)
        )}
      </form>
    );
  }
);

const config = Atom.create({
  constraints: {
    descendants: [
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      {is: 'FormReCaptcha', rule: 'ZeroOrOne'},
    ],
    pinToParent: true,
  },
  displayName: 'Form',
  render: FormForm,
  showEmpty: true,
  dataType: TTypeApplication({
    con: TTypeConstructor(qualify(Plugins.name('Form'), typeName('FormForm'))),
    arg: extraAtomFields,
  }),
  showSettingsInDescendants: true,
});

export default config;
